import { useEffect, useMemo, useState } from "react";
import { http, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    Plus,
    PencilSimple,
    Trash,
    Receipt,
    DownloadSimple,
    MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = [
    { v: "consultoria", l: "Consultoría", code: "C" },
    { v: "desarrollo", l: "Desarrollo", code: "D" },
    { v: "zendesk", l: "Zendesk", code: "Z" },
    { v: "asistencia", l: "Asistencia", code: "A" },
    { v: "recurrente", l: "Recurrente", code: "R" },
];
const TYPE_BY_V = Object.fromEntries(TYPES.map((t) => [t.v, t]));

const STATUSES = [
    { v: "previsto", l: "Previsto", style: "bg-amber-50 text-amber-800 border border-amber-200" },
    { v: "emitida", l: "Emitida", style: "bg-slate-100 text-slate-700" },
    { v: "cobrada", l: "Cobrada", style: "bg-emerald-100 text-emerald-800" },
    { v: "anulada", l: "Anulada", style: "bg-slate-200 text-slate-500 line-through" },
];
const STATUS_BY_V = Object.fromEntries(STATUSES.map((s) => [s.v, s]));

const empty = {
    invoice_number: "",
    invoice_type: "consultoria",
    invoice_date: new Date().toISOString().slice(0, 10),
    project_id: "",
    project_manager_id: "",
    title: "",
    subtitle: "",
    company: "",
    customer_id: "",
    amount: 0,
    currency: "EUR",
    status: "emitida",
    notes: "",
};

function eur(n) {
    return (
        (Number(n) || 0).toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }) + " €"
    );
}

export default function AdminInvoices() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [managers, setManagers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);

    const load = async () => {
        const params = { year };
        if (statusFilter !== "all") params.status_filter = statusFilter;
        if (search.trim()) params.q = search.trim();
        const [inv, sum] = await Promise.all([
            http.get("/invoices", { params }),
            http.get("/invoices/summary", { params: { year } }),
        ]);
        setItems(inv.data);
        setSummary(sum.data);
    };

    useEffect(() => {
        // Cargas de referencia — cada una puede fallar (GET /users es admin-only)
        // así que usamos .catch por petición para no romper la página.
        http.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
        http.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
        http.get("/users/managers")
            .then((r) => setManagers(r.data))
            .catch(() => setManagers([]));
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, statusFilter, search]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            payload.amount = Number(payload.amount);
            if (!payload.project_id) delete payload.project_id;
            if (!payload.project_manager_id) delete payload.project_manager_id;
            if (!payload.customer_id) delete payload.customer_id;
            if (form.invoice_id) {
                await http.put(`/invoices/${form.invoice_id}`, payload);
                toast.success("Factura actualizada");
            } else {
                await http.post("/invoices", payload);
                toast.success("Factura registrada");
            }
            setOpen(false);
            setForm(empty);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const edit = (row) => {
        setForm({
            ...empty,
            ...row,
            project_id: row.project_id || "",
            project_manager_id: row.project_manager_id || "",
            customer_id: row.customer_id || "",
            subtitle: row.subtitle || "",
            invoice_number: row.invoice_number || "",
            company: row.company || "",
            notes: row.notes || "",
        });
        setOpen(true);
    };

    const remove = async (row) => {
        if (
            !window.confirm(
                `¿Eliminar la factura "${row.title}" (${eur(row.amount)})?`,
            )
        )
            return;
        try {
            await http.delete(`/invoices/${row.invoice_id}`);
            toast.success("Eliminada");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const csvUrl = () => {
        const params = new URLSearchParams();
        params.set("year", year);
        if (statusFilter !== "all") params.set("status_filter", statusFilter);
        return `${API}/invoices/export.csv?${params.toString()}`;
    };

    const availableYears = useMemo(() => {
        const s = new Set([year]);
        for (const p of projects) {
            const code = p.project_code || "";
            if (code.length >= 4 && /^\d{4}/.test(code)) {
                s.add(parseInt(code.slice(0, 4), 10));
            }
        }
        for (const it of items) {
            const d = it.invoice_date || "";
            if (d.length >= 4) s.add(parseInt(d.slice(0, 4), 10));
        }
        return Array.from(s).sort((a, b) => b - a);
    }, [projects, items, year]);

    // Sortable table
    const accessors = useMemo(
        () => ({
            type: (r) => TYPE_BY_V[r.invoice_type]?.code || "",
            pm: (r) => r.project_manager_name || "",
            date: (r) => r.invoice_date || "",
            code: (r) => r.project_code || "",
            title: (r) => r.title || "",
            subtitle: (r) => r.subtitle || "",
            company: (r) => r.company || "",
            customer: (r) => r.customer_name || "",
            amount: (r) => Number(r.amount || 0),
            status: (r) => r.status || "",
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        items,
        "invoices",
        accessors,
        { initialKey: "date", initialDir: "desc" },
    );

    return (
        <div data-testid="page-invoices">
            <PageHeader
                eyebrow="Administración"
                title="Facturas emitidas"
                description="Registra las facturas emitidas asociadas a proyectos y sigue su estado (previsto / emitida / cobrada / anulada)."
                actions={
                    <div className="flex gap-2">
                        <a
                            href={csvUrl()}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm flex items-center gap-1 px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm text-slate-700"
                            data-testid="invoices-csv"
                        >
                            <DownloadSimple size={14} /> Exportar CSV
                        </a>
                        <Sheet open={open} onOpenChange={setOpen}>
                            <SheetTrigger asChild>
                                <Button
                                    onClick={() => setForm(empty)}
                                    className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                    data-testid="invoice-new-btn"
                                >
                                    <Plus size={14} className="mr-2" /> Nueva factura
                                </Button>
                            </SheetTrigger>
                            <SheetContent className="rounded-none sm:max-w-md overflow-y-auto">
                                <SheetHeader>
                                    <SheetTitle className="font-display">
                                        {form.invoice_id ? "Editar factura" : "Nueva factura"}
                                    </SheetTitle>
                                    <SheetDescription className="text-xs text-slate-500">
                                        Al elegir proyecto se auto-rellena PM y cliente. Puedes
                                        cambiar cualquier campo manualmente.
                                    </SheetDescription>
                                </SheetHeader>
                                <form onSubmit={submit} className="mt-6 space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs">Tipo</Label>
                                            <Select
                                                value={form.invoice_type}
                                                onValueChange={(v) =>
                                                    setForm({ ...form, invoice_type: v })
                                                }
                                            >
                                                <SelectTrigger
                                                    className="mt-1 rounded-sm border-slate-300"
                                                    data-testid="invoice-type"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {TYPES.map((t) => (
                                                        <SelectItem key={t.v} value={t.v}>
                                                            <span className="font-mono mr-2">
                                                                {t.code}
                                                            </span>
                                                            {t.l}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Fecha</Label>
                                            <Input
                                                type="date"
                                                required
                                                value={form.invoice_date}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        invoice_date: e.target.value,
                                                    })
                                                }
                                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                                data-testid="invoice-date"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Nº factura (opcional)</Label>
                                        <Input
                                            value={form.invoice_number}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    invoice_number: e.target.value,
                                                })
                                            }
                                            placeholder="F-2026/123"
                                            className="mt-1 rounded-sm border-slate-300 font-mono"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Proyecto</Label>
                                        <SearchableSelect
                                            value={form.project_id}
                                            onChange={(v) => {
                                                // Auto-fill pm/customer from project
                                                const p = projects.find((x) => x.project_id === v);
                                                setForm((prev) => ({
                                                    ...prev,
                                                    project_id: v,
                                                    project_manager_id:
                                                        p?.project_manager_id || prev.project_manager_id,
                                                    customer_id: p?.customer_id || prev.customer_id,
                                                }));
                                            }}
                                            options={projects.map((p) => ({
                                                value: p.project_id,
                                                label: p.name,
                                                hint: p.project_code || "",
                                            }))}
                                            placeholder="Buscar proyecto..."
                                            className="mt-1"
                                            data-testid="invoice-project"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">PM responsable</Label>
                                        <SearchableSelect
                                            value={form.project_manager_id}
                                            onChange={(v) =>
                                                setForm({ ...form, project_manager_id: v })
                                            }
                                            options={managers.map((u) => ({
                                                value: u.user_id,
                                                label: u.name,
                                            }))}
                                            placeholder="Sin asignar"
                                            className="mt-1"
                                            data-testid="invoice-pm"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Título</Label>
                                        <Input
                                            required
                                            value={form.title}
                                            onChange={(e) =>
                                                setForm({ ...form, title: e.target.value })
                                            }
                                            className="mt-1 rounded-sm border-slate-300"
                                            data-testid="invoice-title"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Subtítulo (opcional)</Label>
                                        <Input
                                            value={form.subtitle}
                                            onChange={(e) =>
                                                setForm({ ...form, subtitle: e.target.value })
                                            }
                                            placeholder="Ej. 1º 50%, Entrega Final (30%)"
                                            className="mt-1 rounded-sm border-slate-300"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Empresa (destinataria)</Label>
                                        <Input
                                            value={form.company}
                                            onChange={(e) =>
                                                setForm({ ...form, company: e.target.value })
                                            }
                                            placeholder="Ej. Vectalia, DFB Transportes..."
                                            className="mt-1 rounded-sm border-slate-300"
                                            data-testid="invoice-company"
                                        />
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            Quien recibe la factura. Puede diferir del cliente
                                            final del proyecto.
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Cliente final</Label>
                                        <SearchableSelect
                                            value={form.customer_id}
                                            onChange={(v) =>
                                                setForm({ ...form, customer_id: v })
                                            }
                                            options={customers.map((c) => ({
                                                value: c.customer_id,
                                                label: c.name,
                                                hint: c.code || "",
                                            }))}
                                            placeholder="—"
                                            className="mt-1"
                                            data-testid="invoice-customer"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs">Importe (€)</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                required
                                                value={form.amount}
                                                onChange={(e) =>
                                                    setForm({ ...form, amount: e.target.value })
                                                }
                                                className="mt-1 rounded-sm border-slate-300 font-mono text-right"
                                                data-testid="invoice-amount"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Estado</Label>
                                            <Select
                                                value={form.status}
                                                onValueChange={(v) =>
                                                    setForm({ ...form, status: v })
                                                }
                                            >
                                                <SelectTrigger
                                                    className="mt-1 rounded-sm border-slate-300"
                                                    data-testid="invoice-status"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {STATUSES.map((s) => (
                                                        <SelectItem key={s.v} value={s.v}>
                                                            {s.l}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Notas (opcional)</Label>
                                        <Textarea
                                            rows={2}
                                            value={form.notes}
                                            onChange={(e) =>
                                                setForm({ ...form, notes: e.target.value })
                                            }
                                            className="mt-1 rounded-sm border-slate-300"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                        data-testid="invoice-save"
                                    >
                                        {form.invoice_id ? "Guardar" : "Registrar"}
                                    </Button>
                                </form>
                            </SheetContent>
                        </Sheet>
                    </div>
                }
            />
            <div className="p-6 lg:p-10 space-y-5">
                {/* Summary widgets */}
                {summary && (
                    <div
                        className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200"
                        data-testid="invoices-summary"
                    >
                        <div className="bg-white p-4">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Total {year}
                            </div>
                            <div className="font-mono text-2xl font-bold mt-2">
                                {eur(summary.total)}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                {summary.count} facturas
                            </div>
                        </div>
                        {STATUSES.map((s) => (
                            <div key={s.v} className="bg-white p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    {s.l}
                                </div>
                                <div className="font-mono text-lg font-semibold mt-2">
                                    {eur(summary.by_status[s.v]?.amount || 0)}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-1">
                                    {summary.by_status[s.v]?.count || 0} facturas
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 border border-slate-200 bg-white p-3">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MagnifyingGlass
                            size={14}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <Input
                            placeholder="Buscar por título, código, empresa..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="rounded-sm border-slate-300 pl-7 h-8"
                            data-testid="invoices-search"
                        />
                    </div>
                    <Select
                        value={String(year)}
                        onValueChange={(v) => setYear(Number(v))}
                    >
                        <SelectTrigger
                            className="rounded-sm border-slate-300 w-32 h-8"
                            data-testid="invoices-year"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availableYears.map((y) => (
                                <SelectItem key={y} value={String(y)}>
                                    {y}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger
                            className="rounded-sm border-slate-300 w-40 h-8"
                            data-testid="invoices-status-filter"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los estados</SelectItem>
                            {STATUSES.map((s) => (
                                <SelectItem key={s.v} value={s.v}>
                                    {s.l}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-500 ml-auto font-mono">
                        {sorted.length} facturas
                    </span>
                </div>

                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid="invoices-table">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("type", "py-3 px-3 text-left w-12")}>
                                    Tipo
                                    <SortIndicator active={sortKey === "type"} dir={sortDir} />
                                </th>
                                <th {...headerProps("pm", "py-3 px-3 text-left")}>
                                    Nº fac / PM
                                    <SortIndicator active={sortKey === "pm"} dir={sortDir} />
                                </th>
                                <th {...headerProps("date", "py-3 px-3 text-left")}>
                                    Fecha
                                    <SortIndicator active={sortKey === "date"} dir={sortDir} />
                                </th>
                                <th {...headerProps("code", "py-3 px-3 text-left")}>
                                    Código
                                    <SortIndicator active={sortKey === "code"} dir={sortDir} />
                                </th>
                                <th {...headerProps("title", "py-3 px-3 text-left")}>
                                    Título
                                    <SortIndicator active={sortKey === "title"} dir={sortDir} />
                                </th>
                                <th {...headerProps("subtitle", "py-3 px-3 text-left")}>
                                    Subtítulo
                                    <SortIndicator
                                        active={sortKey === "subtitle"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("company", "py-3 px-3 text-left")}>
                                    Empresa
                                    <SortIndicator
                                        active={sortKey === "company"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("customer", "py-3 px-3 text-left")}>
                                    Cliente
                                    <SortIndicator
                                        active={sortKey === "customer"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("amount", "py-3 px-3 text-right")}>
                                    Cantidad
                                    <SortIndicator active={sortKey === "amount"} dir={sortDir} />
                                </th>
                                <th {...headerProps("status", "py-3 px-3 text-left")}>
                                    Estado
                                    <SortIndicator active={sortKey === "status"} dir={sortDir} />
                                </th>
                                <th className="py-3 px-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={11}
                                        className="text-center py-16 text-slate-500"
                                    >
                                        <Receipt
                                            size={28}
                                            className="mx-auto text-slate-300 mb-3"
                                        />
                                        No hay facturas para {year}.
                                    </td>
                                </tr>
                            )}
                            {sorted.map((r) => {
                                const t = TYPE_BY_V[r.invoice_type];
                                const s = STATUS_BY_V[r.status];
                                return (
                                    <tr
                                        key={r.invoice_id}
                                        className={`border-b border-slate-100 hover:bg-slate-50 ${r.status === "cobrada" ? "bg-emerald-50/40" : ""} ${r.status === "anulada" ? "opacity-60" : ""}`}
                                        data-testid={`invoice-row-${r.invoice_id}`}
                                    >
                                        <td className="py-2 px-3">
                                            <span
                                                className="inline-flex items-center justify-center w-6 h-6 rounded-sm bg-slate-100 text-slate-800 font-mono text-xs font-bold"
                                                title={t?.l}
                                            >
                                                {t?.code || "—"}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3">
                                            {r.invoice_number ? (
                                                <div className="font-mono text-slate-800 text-xs">
                                                    {r.invoice_number}
                                                </div>
                                            ) : null}
                                            {r.project_manager_name && (
                                                <div className="text-[11px] text-slate-500">
                                                    {r.project_manager_name}
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 font-mono text-slate-700 text-xs whitespace-nowrap">
                                            {r.invoice_date}
                                        </td>
                                        <td className="py-2 px-3 font-mono text-slate-700 text-xs whitespace-nowrap">
                                            {r.project_code || "—"}
                                        </td>
                                        <td className="py-2 px-3 text-slate-900 max-w-xs">
                                            <div className="truncate" title={r.title}>
                                                {r.title}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 text-slate-600 max-w-xs">
                                            <div className="truncate" title={r.subtitle}>
                                                {r.subtitle || "—"}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 text-slate-700 max-w-[180px]">
                                            <div className="truncate" title={r.company}>
                                                {r.company || "—"}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 text-slate-700 max-w-[180px]">
                                            <div className="truncate" title={r.customer_name}>
                                                {r.customer_name || "—"}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 text-right font-mono tabular-nums font-semibold text-slate-900 whitespace-nowrap">
                                            {eur(r.amount)}
                                        </td>
                                        <td className="py-2 px-3">
                                            <span
                                                className={`px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider ${s?.style || ""}`}
                                            >
                                                {s?.l || r.status}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => edit(r)}
                                                className="text-slate-500 hover:text-slate-900 mr-3"
                                                data-testid={`invoice-edit-${r.invoice_id}`}
                                            >
                                                <PencilSimple size={16} />
                                            </button>
                                            {isAdmin && (
                                                <button
                                                    onClick={() => remove(r)}
                                                    className="text-slate-400 hover:text-red-600"
                                                    data-testid={`invoice-remove-${r.invoice_id}`}
                                                >
                                                    <Trash size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
